/**
 * Offline simulation of the pipeline service for local dev / Storybook / tests.
 *
 * Each triggered run advances pending → running → completed (or failed when
 * the test outcome override is set) on successive status fetches, so the 5s
 * polling loop can be exercised without the real service.
 */

import type {
  PipelineRun,
  PipelineRunListResponse,
  PipelineRunStatus,
  TriggerPipelineRequest,
  TriggerPipelineResponse,
} from "./pipeline.types";

const MOCK_RUN_ID = "00000000-0000-4000-8000-00000000mock";
const MOCK_WORKSPACE_ID = "3f86f28c-bd41-4a47-acc3-3b06eb2a7d77";

let lastWorkspaceId = MOCK_WORKSPACE_ID;
const MOCK_FAILURE_MESSAGE =
  "Entity extraction step failed: source table not found";

type MockOutcome = Extract<PipelineRunStatus, "completed" | "failed">;

let pollCount = 0;
let outcome: MockOutcome = "completed";

export const __setMockPipelineOutcomeForTests__ = (
  value: MockOutcome,
): void => {
  outcome = value;
};

export const __resetMockPipelineForTests__ = (): void => {
  pollCount = 0;
  outcome = "completed";
  lastWorkspaceId = MOCK_WORKSPACE_ID;
};

export const mockTriggerPipeline = async (
  _request?: TriggerPipelineRequest,
): Promise<TriggerPipelineResponse> => {
  pollCount = 0;
  return { run_id: MOCK_RUN_ID, status: "pending", created: true };
};

export const mockFetchPipelineRun = async (
  runId: string,
): Promise<PipelineRun> => {
  pollCount += 1;
  const status: PipelineRunStatus =
    pollCount === 1 ? "pending" : pollCount === 2 ? "running" : outcome;
  const isTerminal = status === "completed" || status === "failed";

  return {
    run_id: runId,
    workspace_id: lastWorkspaceId,
    status,
    trigger: "manual",
    started_at: pollCount > 1 ? "2026-06-09T04:12:32.175619Z" : null,
    completed_at: isTerminal ? "2026-06-09T04:13:46.259595Z" : null,
    error_message: status === "failed" ? MOCK_FAILURE_MESSAGE : null,
    created_at: "2026-06-09T04:12:32.051190Z",
  };
};

/**
 * The workspace's newest run: one finished build. Fixed rather than advancing, so this
 * background poll never moves the trigger flow's poll count above.
 */
export const mockFetchLatestPipelineRun = async (
  workspaceId: string,
): Promise<PipelineRunListResponse> => ({
  items: [
    {
      id: MOCK_RUN_ID,
      workspace_id: workspaceId,
      status: "completed",
      trigger: "connector_sync",
      started_at: "2026-06-09T04:12:32.175619Z",
      completed_at: "2026-06-09T04:13:46.259595Z",
      error_message: null,
      created_at: "2026-06-09T04:12:32.051190Z",
    },
  ],
  total: 1,
  limit: 1,
  offset: 0,
});
